import { useRef } from 'react';
import { Reveal } from '../ui/Reveal';
import art from './Art.module.css';
import styles from './Emblems.module.css';

/** I — Bespoke: parts drafted on a grid, the final piece slides into place. */
function Assembly() {
  const dots = [];
  for (let x = 20; x <= 140; x += 20) for (let y = 20; y <= 140; y += 20) dots.push([x, y]);

  return (
    <svg className={art.svg} viewBox="0 0 160 160" focusable="false">
      <g className={art.fade}>
        {dots.map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="0.9" className={styles.dot} />
        ))}
      </g>
      <rect x="20" y="20" width="80" height="40" rx="3" pathLength="1" className={art.draw} />
      <rect x="110" y="20" width="30" height="60" rx="3" pathLength="1" className={art.draw} style={{ '--d': '250ms' }} />
      <rect x="20" y="70" width="40" height="70" rx="3" pathLength="1" className={art.draw} style={{ '--d': '500ms' }} />
      <g className={styles.piece}>
        <rect x="70" y="90" width="70" height="50" rx="3" className={`${art.accent} ${styles.pieceFill}`} />
        <path d="M84 115 H126 M105 104 V126" className={art.accent} />
      </g>
      <path d="M70 70 H100 V80" pathLength="1" className={`${art.draw} ${art.faint}`} style={{ '--d': '800ms' }} />
    </svg>
  );
}

const lines = [
  { tangled: 'M20 50 C60 130, 90 10, 140 112', clean: 'M20 50 C60 50, 90 50, 140 50' },
  { tangled: 'M20 80 C50 10, 100 150, 140 30', clean: 'M20 80 C50 80, 100 80, 140 80', accent: true },
  { tangled: 'M20 110 C70 20, 80 140, 140 72', clean: 'M20 110 C70 110, 80 110, 140 110' },
];

/** II — Modernization: tangled legacy wiring resolves into clean, parallel routes. */
function Untangle({ svgRef }) {
  return (
    <svg ref={svgRef} className={art.svg} viewBox="0 0 160 160" focusable="false">
      <path d="M20 20 V140 M140 20 V140" className={art.faint} />
      {lines.map(({ tangled, clean, accent }) => {
        const endY = (d) => d.split(' ').at(-1);
        return (
          <g key={clean} className={accent ? art.accent : undefined}>
            <path d={tangled}>
              <animate attributeName="d" to={clean} dur="1.6s" begin="indefinite" fill="freeze" calcMode="spline" keyTimes="0;1" keySplines="0.65 0 0.35 1" />
            </path>
            <circle cx="20" cy={clean.split(' ')[1]} r="3" className={styles.node} />
            <circle cx="140" cy={endY(tangled)} r="3" className={styles.node}>
              <animate attributeName="cy" to={endY(clean)} dur="1.6s" begin="indefinite" fill="freeze" calcMode="spline" keyTimes="0;1" keySplines="0.65 0 0.35 1" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

const ticks = Array.from({ length: 13 }, (_, i) => -120 + i * 20);

/** III — Refinement: a performance gauge whose needle swings from slow to fast. */
function Gauge() {
  return (
    <svg className={art.svg} viewBox="0 0 160 160" focusable="false">
      <path d="M32.4 107.5 A55 55 0 1 1 127.6 107.5" pathLength="1" className={art.draw} />
      <path d="M127.6 52.5 A55 55 0 0 1 127.6 107.5" pathLength="1" className={`${art.draw} ${art.accent} ${styles.fastArc}`} style={{ '--d': '600ms' }} />
      <g className={art.fade} style={{ '--d': '300ms' }}>
        {ticks.map((deg) => (
          <line key={deg} x1="80" y1="36" x2="80" y2={deg % 60 === 0 ? 46 : 42} transform={`rotate(${deg} 80 80)`} />
        ))}
      </g>
      <g className={styles.needle}>
        <path d="M80 80 L80 34" className={art.accent} />
        <circle cx="80" cy="80" r="4" className={styles.hub} />
      </g>
      <text x="80" y="128" textAnchor="middle" className={`${art.label} ${art.fade}`} style={{ '--d': '1200ms' }}>
        Load time
      </text>
    </svg>
  );
}

export function Emblem({ practice }) {
  const svgRef = useRef(null);
  const play = () =>
    svgRef.current?.querySelectorAll('animate').forEach((a) => a.beginElement?.());

  return (
    <Reveal className={styles.emblem} onReveal={practice === 'modernization' ? () => setTimeout(play, 400) : undefined} aria-hidden="true">
      {practice === 'bespoke' && <Assembly />}
      {practice === 'modernization' && <Untangle svgRef={svgRef} />}
      {practice === 'refinement' && <Gauge />}
    </Reveal>
  );
}
